/**
 * Hover explanations for every control — one mechanism, one place to change it.
 *
 * ActOS does things that are expensive or irreversible: spending a data-vendor
 * credit, blocking a whole company's domain, approving a version of a Library
 * object that then becomes immutable, deactivating the last admin. A rep who
 * has to click something to learn what it does will learn it the expensive way.
 *
 * **Why an attribute rather than a component.** A `<Tooltip>` wrapper would put
 * an extra element inside every flex row, grid cell and table cell in the app,
 * and quietly change layouts that are already right. `data-tip` is inert: it
 * adds one attribute to the control and nothing else.
 *
 * **Why a positioner rather than a `::after`.** The bubble used to be a
 * pseudo-element on the control itself, which made it a child of whatever the
 * control was inside. Every scroll container in the app — `.stage`,
 * `.queue-rail`, `.drawer`, `.notice-menu` — then clipped it, and `.app`'s
 * `overflow: hidden` clipped whatever escaped those. Alignment was chosen by
 * hand at each call site via `tipAt`, and on one screen 12 of 27 bubbles were
 * measured landing outside their container. A single `position: fixed` bubble
 * owned by the document has no clipping ancestor and can measure the viewport,
 * so placement is computed rather than guessed.
 *
 * That also fixes three things the pseudo-element could not:
 *   - `content: attr(data-tip)` was live in the accessibility tree even at
 *     `opacity: 0`, so every tipped control's name was its label plus a
 *     paragraph. The bubble is now referenced by `aria-describedby` only while
 *     it is shown.
 *   - WCAG 1.4.13 wants a tooltip to be dismissable, and there was no way to
 *     dismiss one; Escape now does it. The same criterion's "hoverable" prong
 *     is knowingly NOT met: the bubble keeps `pointer-events: none`, because
 *     it is placed 8px below its control and would otherwise sit over the next
 *     task row and swallow the first click aimed at it. In a queue where the
 *     controls underneath approve mail, eating a click is the worse failure.
 *   - The arrow was centred on the control while the bubble was edge-aligned,
 *     so on a wide control the two came apart.
 *
 * Keep the text explaining CONSEQUENCE, not appearance. "Blocks everyone at
 * this company" earns its place; "Click to block" does not.
 */

/**
 * Styled tooltip. Spread onto any non-replaced element — button, label, `<a>`,
 * `<span>`, a table cell.
 *
 *   <button type="button" {...tip(vendor.pricing.reveal)}>Reveal</button>
 */
export function tip(text: string) {
  return { 'data-tip': text } as const;
}

/**
 * State a placement PREFERENCE. The positioner honours it when the bubble fits
 * and overrides it when it does not, so this can no longer put a bubble
 * off-screen — it is a nicety now, not a correctness requirement.
 *
 * Retained because ~190 call sites pass it, and because the preference is still
 * the right first guess: a control pinned to the right margin does look better
 * with its bubble right-aligned even though clamping would have saved it.
 */
export function tipAt(text: string, where: 'start' | 'end' | 'top') {
  return where === 'top'
    ? ({ 'data-tip': text, 'data-tip-pos': 'top' } as const)
    : ({ 'data-tip': text, 'data-tip-align': where } as const);
}

/**
 * Native tooltip. Kept for the few places that want the browser's own bubble;
 * `tip()` now works on replaced elements too, so prefer it.
 */
export function titleTip(text: string) {
  return { title: text } as const;
}

/* -------------------------------------------------------------------------
 * The positioner.
 * ---------------------------------------------------------------------- */

const SHOW_DELAY = 320;
/** Long enough for the pointer to cross the 8px gap onto the bubble. */
const HIDE_DELAY = 140;
const GAP = 8;
/** Keep the bubble this far off the viewport edge when clamping. */
const MARGIN = 8;
const BUBBLE_ID = 'actos-tip';
const DESC_ID = 'actos-tip-desc';

let bubble: HTMLDivElement | null = null;
let arrow: HTMLDivElement | null = null;
let desc: HTMLSpanElement | null = null;
let current: HTMLElement | null = null;
/** The element `aria-describedby` was written onto — the focused control, which
 *  is not always the anchor: the anchor may be a wrapping <label>. */
let described: Element | null = null;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let watcher: MutationObserver | null = null;

function ensureBubble(): HTMLDivElement {
  if (bubble) return bubble;
  bubble = document.createElement('div');
  bubble.id = BUBBLE_ID;
  bubble.className = 'tip-bubble';
  /*
   * Purely visual. The description a screen reader actually reads lives in the
   * separate `.tip-desc` span below, because this element is `hidden` between
   * hovers and a hidden node contributes nothing to the accessibility tree —
   * pointing `aria-describedby` at it announced nothing at all.
   */
  bubble.setAttribute('aria-hidden', 'true');
  bubble.hidden = true;

  arrow = document.createElement('div');
  arrow.className = 'tip-arrow';
  bubble.appendChild(arrow);

  const text = document.createElement('span');
  text.className = 'tip-text';
  bubble.appendChild(text);

  document.body.appendChild(bubble);

  /*
   * The accessible description: off-screen, never `hidden`, so it is always in
   * the accessibility tree and `aria-describedby` resolves the moment focus
   * lands. Announcing it 320ms later — when the visual bubble appears — is too
   * late; the control's focus event has already been spoken.
   */
  desc = document.createElement('span');
  desc.id = DESC_ID;
  desc.className = 'tip-desc';
  document.body.appendChild(desc);

  return bubble;
}

/**
 * Associate the description with the element that actually has focus.
 *
 * Not with the anchor: at 39 call sites the anchor is a wrapping
 * `<label className="field">`, and `aria-describedby` on a label is not
 * conveyed to the control inside it, so those explanations were silent.
 */
function describe(target: Element, text: string) {
  ensureBubble();
  if (desc) desc.textContent = text;
  if (described && described !== target) described.removeAttribute('aria-describedby');
  described = target;
  target.setAttribute('aria-describedby', DESC_ID);
}

function undescribe() {
  if (described) {
    described.removeAttribute('aria-describedby');
    described = null;
  }
  if (desc) desc.textContent = '';
}

/**
 * Hide if the anchor leaves the document.
 *
 * React unmounts the control under a live bubble routinely — approving a draft
 * removes its row, closing a drawer removes everything in it. Without this the
 * bubble stayed on screen pointing at nothing until the next pointer event, and
 * `aria-describedby` was left on a detached node.
 */
function watch(anchor: HTMLElement) {
  watcher?.disconnect();
  watcher = new MutationObserver(() => {
    if (!anchor.isConnected) hide();
  });
  watcher.observe(document.body, { childList: true, subtree: true });
}

function clearShow() {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
}

function clearHide() {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function scheduleHide() {
  clearHide();
  hideTimer = setTimeout(hide, HIDE_DELAY);
}

function hide() {
  clearShow();
  clearHide();
  watcher?.disconnect();
  watcher = null;
  current = null;
  undescribe();
  if (bubble) {
    bubble.classList.remove('is-open');
    bubble.hidden = true;
  }
}

/**
 * Place the bubble against `anchor`.
 *
 * Vertical: the preferred side is below (or above for `data-tip-pos="top"`),
 * and it flips when the preferred side does not fit. If neither side fits the
 * roomier one wins, so a bubble is never placed entirely off-screen.
 *
 * Horizontal: the preference aligns one edge to the anchor, then the result is
 * clamped into the viewport. The arrow tracks the anchor's centre independently
 * so it stays attached however far the bubble was clamped.
 */
function place(anchor: HTMLElement) {
  const el = ensureBubble();
  const a = anchor.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  // Measure at the natural width, unconstrained by any previous placement.
  el.style.left = '0px';
  el.style.top = '0px';
  el.style.maxWidth = `${Math.min(336, vw - MARGIN * 2)}px`;
  const b = el.getBoundingClientRect();

  const prefersTop = anchor.getAttribute('data-tip-pos') === 'top';
  const roomBelow = vh - a.bottom - GAP;
  const roomAbove = a.top - GAP;
  const fitsBelow = roomBelow >= b.height;
  const fitsAbove = roomAbove >= b.height;

  let above: boolean;
  if (prefersTop) above = fitsAbove || !fitsBelow;
  else above = !fitsBelow && (fitsAbove || roomAbove > roomBelow);

  const top = above ? a.top - GAP - b.height : a.bottom + GAP;

  const align = anchor.getAttribute('data-tip-align');
  let left = align === 'end' ? a.right - b.width : a.left;
  left = Math.max(MARGIN, Math.min(left, vw - MARGIN - b.width));

  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(Math.max(MARGIN, Math.min(top, vh - MARGIN - b.height)))}px`;
  el.classList.toggle('is-above', above);

  if (arrow) {
    // Clamp to the bubble's rounded corners so the arrow never overhangs.
    const centre = a.left + a.width / 2 - left;
    arrow.style.left = `${Math.round(Math.max(12, Math.min(centre, b.width - 12)))}px`;
  }
}

function show(anchor: HTMLElement) {
  const text = anchor.getAttribute('data-tip');
  // The anchor can be gone by the time the delay elapses — a row that was
  // hovered and then removed by a refetch, for instance.
  if (!text || !anchor.isConnected) return;
  const el = ensureBubble();
  const span = el.querySelector('.tip-text');
  if (span) span.textContent = text;

  current = anchor;
  watch(anchor);

  el.hidden = false;
  place(anchor);
  // Next frame, so the transition has a from-state to animate out of.
  requestAnimationFrame(() => el.classList.add('is-open'));
}

function anchorFrom(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  // `closest` is what makes a tip on `<label className="field">` reachable by
  // keyboard: focus lands on the inner <input>, and the label is its ancestor.
  const el = target.closest<HTMLElement>('[data-tip]');
  return el && el.getAttribute('data-tip') ? el : null;
}

function onEnter(e: Event) {
  const anchor = anchorFrom(e.target);
  if (!anchor) return;

  if (e.type === 'focusin' && e.target instanceof Element) {
    /*
     * Associate the description on ANY focus, keyboard or not, and do it now
     * rather than when the bubble appears 320ms later — by then the control's
     * focus event has already been spoken. This half must not be gated on
     * `:focus-visible`: a user who clicks into a field and reads it with a
     * screen reader needs the explanation exactly as much as one who tabs to it.
     */
    const text = anchor.getAttribute('data-tip');
    if (text) describe(e.target, text);

    /*
     * The VISIBLE bubble is gated, which is the rule the old CSS had via
     * `:focus-visible`. It is load-bearing rather than cosmetic: Dialog and
     * useRevealOnMount both move focus programmatically, and a bubble raised by
     * that set `current`, after which the Escape handler below swallowed the
     * user's first Escape — so the drawer they were trying to close ignored it.
     */
    if (!e.target.matches(':focus-visible')) return;
  }

  if (anchor === current) {
    clearHide();
    return;
  }
  clearShow();
  clearHide();
  showTimer = setTimeout(() => show(anchor), SHOW_DELAY);
}

function onLeave(e: Event) {
  const anchor = anchorFrom(e.target);
  if (!anchor) return;
  /*
   * `pointerout` and `focusout` also fire when moving BETWEEN children of the
   * same control — a button with a `<span>` inside is enough. Without this
   * guard, crossing from the label span to the button's own padding cancelled
   * the pending show, and a tip on any composite control never appeared.
   */
  const related = (e as PointerEvent | FocusEvent).relatedTarget;
  if (related instanceof Node && anchor.contains(related)) return;

  clearShow();
  scheduleHide();
}

/**
 * Install the delegated listeners. Called once from main.tsx — the bubble is
 * document-owned rather than React-owned so that no component has to render it
 * and no layout can clip it.
 */
export function installTooltips() {
  document.addEventListener('pointerover', onEnter, true);
  document.addEventListener('pointerout', onLeave, true);
  document.addEventListener('focusin', onEnter, true);
  document.addEventListener('focusout', onLeave, true);

  // A pointer that presses something has stopped asking what it does.
  document.addEventListener('pointerdown', hide, true);

  // Anything that moves the anchor invalidates the placement. Hiding is
  // cheaper and less distracting than tracking it.
  window.addEventListener('scroll', hide, true);
  window.addEventListener('resize', hide);

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape') return;
      // Only swallow Escape when a bubble is actually up. The account drawer
      // and the enrollment wizard both close on Escape, and a rep dismissing a
      // tooltip must not lose the drawer they were reading it inside.
      if (!current) return;
      /*
       * `stopImmediatePropagation`, not `stopPropagation`. Dialog registers its
       * own Escape handler on `document` in the capture phase too, and
       * `stopPropagation` does not stop other listeners bound to the SAME node
       * in the SAME phase — so the drawer closed along with the tooltip. This
       * listener is installed at module scope before React mounts, so it runs
       * first and this call is what actually wins.
       */
      e.stopImmediatePropagation();
      hide();
    },
    true,
  );
}
